/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState, type FC } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useBotInfoStore } from '@coze-studio/bot-detail-store/bot-info';
import { I18n } from '@coze-arch/i18n';
import { BotDebugButton } from '@coze-agent-ide/space-bot/component';

import { getPublishConfig } from '../../api/published-apps';
import {
  AppStoreScopeModal,
  type AppStoreScopeModalRef,
} from './app-store-scope-modal';

export const AppStoreScopeButton: FC = () => {
  const { botId, botName } = useBotInfoStore(
    useShallow(state => ({
      botId: state.botId,
      botName: state.name,
    })),
  );
  const [visible, setVisible] = useState(false);
  const modalRef = useRef<AppStoreScopeModalRef>(null);

  useEffect(() => {
    if (!botId) {
      setVisible(false);
      return;
    }
    let cancelled = false;
    void getPublishConfig(botId).then(result => {
      if (!cancelled) {
        setVisible(Boolean(result.found && result.id));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [botId]);

  if (!visible || !botId) {
    return null;
  }

  return (
    <>
      <BotDebugButton
        data-testid="agent-ide.app-store-scope-button"
        color="secondary"
        onClick={() =>
          modalRef.current?.open({
            botId,
            title: `${I18n.t('abr_app_store_scope_modal_title')} · ${botName || ''}`,
          })
        }
      >
        {I18n.t('abr_app_store_view_scope')}
      </BotDebugButton>
      <AppStoreScopeModal ref={modalRef} />
    </>
  );
};
